import { useEffect, useId, useState } from "react";
import { TextInput } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import type { SingleQuestionDefinition } from "@ferrivia/api-contract";
import { focusWithoutPan } from "../../onboarding/focusWithoutPan";
import f from "./fieldBox.module.css";
import s from "./NumberQuestion.module.css";

export interface NumberQuestionProps {
  question: SingleQuestionDefinition;
  labelledBy: string;
  value: number | undefined;
  onChange: (value: number) => void;
  unit?: string;
  /** e.g. SupplementPicker's "Valmisteen etiketti: 100 mg / tabletti" on
      `daily_iron_dose` -- the manufacturer's label figure, display only.
      Never derives or prefills `value` from it; the field stays whatever
      the client themselves typed. */
  helperText?: string;
  errorText?: string;
}

/** Mirrors the server's own numeric constraints (`integer`/`min`/`max`,
    checked again by `saveAnswers`) so a 400 is genuinely exceptional rather
    than the normal path for a value outside the range. Keeps a local
    `raw` string rather than deriving the input's displayed value from the
    committed number: a keystroke mid-way through typing "-5" or "12" can be
    numerically invalid for an instant, and rejecting it there would make
    the field feel broken rather than just declining to save yet. */
export function NumberQuestion({ question, labelledBy, value, onChange, unit, helperText, errorText }: NumberQuestionProps) {
  const { t } = useTranslation("client");
  const [raw, setRaw] = useState(value !== undefined ? String(value) : "");
  const helperId = useId();

  useEffect(() => {
    setRaw(value !== undefined ? String(value) : "");
  }, [value]);

  function handleChange(next: string) {
    setRaw(next);
    if (next === "" || next === "-") return;
    const parsed = Number(next);
    if (Number.isNaN(parsed)) return;
    if (question.integer && !Number.isInteger(parsed)) return;
    if (question.min !== undefined && parsed < question.min) return;
    if (question.max !== undefined && parsed > question.max) return;
    onChange(parsed);
  }

  /** type="number" alone still lets a decimal point/exponent through, so an
      integer question would silently refuse to commit "2.5" while leaving it
      sitting in the box. Blocking the keystroke instead keeps the field
      truthful about what it accepts. */
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (question.integer && (event.key === "." || event.key === "," || event.key === "e" || event.key === "E")) {
      event.preventDefault();
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    if (!question.integer) return;
    const pasted = event.clipboardData.getData("text");
    if (/^-?\d*$/.test(pasted)) return;
    event.preventDefault();
    const target = event.currentTarget;
    const start = target.selectionStart ?? raw.length;
    const end = target.selectionEnd ?? raw.length;
    const sanitized = pasted.replace(/[^0-9-]/g, "");
    handleChange(raw.slice(0, start) + sanitized + raw.slice(end));
  }

  return (
    <div className={s.row}>
      <TextInput
        className={`${f.box} ${s.input}`}
        aria-labelledby={labelledBy}
        aria-describedby={helperText ? helperId : undefined}
        placeholder={t("questionnaire.numberPlaceholder", "Syötä luku")}
        type="number"
        inputMode={question.integer ? "numeric" : "decimal"}
        min={question.min}
        max={question.max}
        step={question.integer ? 1 : "any"}
        value={raw}
        onMouseDown={focusWithoutPan}
        aria-invalid={Boolean(errorText)}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
      {unit ? <span className={s.unit}>{unit}</span> : null}
      {helperText ? (
        <span id={helperId} className={s.helper}>
          {helperText}
        </span>
      ) : null}
      {errorText ? (
        <span role="alert" className={s.error}>
          {errorText}
        </span>
      ) : null}
    </div>
  );
}
